import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onClose"];
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css, theme } from 'academy-common-lib/utils/Styling';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LORAX } from 'HubStyleTokens/colors';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIconButton from 'UIComponents/button/UIIconButton';
import { useIsPartner } from '../../partners';
import UsageTracker from '../../../utils/UsageTracker';
import { PAGE_NAMES } from '../../../constants/Navigation';
import AcademyToSoftware from '../../../containers/AcademyToSoftware/AcademyToSoftware';
import { isHubSpotNativeApp } from '../../../utils/utils';
import I18n from 'I18n';
import { useNavigationState } from '../navbarQueries';
import { NAVBAR_ITEM_URLS } from '../navbarConstants';
import { Dropdown, DropdownButton, DropdownButtonItem, DropdownLinkItem, useDropdownState } from './MobileDropdown';
import FullScreenModal from '../../../components/Modals/FullScreenModal/FullScreenModal';
import { NavbarSavedItemsTooltip } from '../../savedItems';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DROPDOWN_HEIGHT = 44;
const MobileDropdownButton = styled(DropdownButton).withConfig({
  displayName: "NavbarMobileTabs__MobileDropdownButton",
  componentId: "sc-18925bl-0"
})(["color:", " !important;font-size:13px;margin-left:12px;height:", "px;"], theme.colors.OLAF, DROPDOWN_HEIGHT);
const buttonItemStyles = css(["padding:12px 20px 12px 0 !important;border-bottom:1px solid ", " !important;font-weight:400;height:44px;width:calc(100% - 24px);margin-left:24px !important;color:", " !important;-webkit-font-smoothing:unset;"], theme.colors.HEFFALUMP, theme.colors.OLAF);
const MobileDropdownButtonItem = styled(DropdownButtonItem).withConfig({
  displayName: "NavbarMobileTabs__MobileDropdownButtonItem",
  componentId: "sc-18925bl-1"
})(["", ""], buttonItemStyles);
const MobileDropdownLinkItem = styled(DropdownLinkItem).withConfig({
  displayName: "NavbarMobileTabs__MobileDropdownLinkItem",
  componentId: "sc-18925bl-2"
})(["padding:12px 20px 12px 0 !important;border-bottom:1px solid ", " !important;font-weight:400;height:44px;width:100%;margin-left:24px !important;color:", " !important;-webkit-font-smoothing:unset;display:block;"], theme.colors.HEFFALUMP, theme.colors.OLAF);
const MobileMenu = styled(FullScreenModal).withConfig({
  displayName: "NavbarMobileTabs__MobileMenu",
  componentId: "sc-18925bl-3"
})(["background-color:", ";margin-top:", "px;overflow:hidden;"], theme.colors.FLAT_EARTH, DROPDOWN_HEIGHT);
const ClickCatcher = styled.button.withConfig({
  displayName: "NavbarMobileTabs__ClickCatcher",
  componentId: "sc-18925bl-4"
})(["position:fixed;top:0;left:0;right:0;height:48px;z-index:0;opacity:0;display:", ";"], ({
  $show
}) => $show ? 'block' : 'none');
const MobileDropdownMenu = _ref => {
  let {
      children,
      onClose
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    toggle,
    show
  } = useDropdownState();
  const handleClose = useCallback(() => {
    toggle();
    onClose();
  }, [onClose, toggle]);
  return /*#__PURE__*/_jsxs("nav", {
    "aria-hidden": !show,
    children: [/*#__PURE__*/_jsx(ClickCatcher, {
      $show: show,
      "aria-label": I18n.text('academyNav.close'),
      className: "click-catcher",
      "data-test-id": "mobile-dropdown-close-button",
      onClick: handleClose
    }), /*#__PURE__*/_jsx(MobileMenu, Object.assign({
      open: show,
      showCloseButton: false
    }, props, {
      children: children
    }))]
  });
};
const handleDropdownOpen = () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-open'
  });
};
const handleDropdownClose = () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-close'
  });
};
const handleDropdownItemClick = urlLocation => () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-item-clicked',
    item: urlLocation
  });
};
const handleAcademyToSoftwareClick = () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-academy-to-software-click'
  });
};
const getPageI18nKey = pageName => {
  const i18nKeys = {
    [PAGE_NAMES.CATALOG]: 'academyNav.heading.mobileNavLinkTitles.tracks',
    [PAGE_NAMES.MY_LEARNING]: 'academyNav.heading.mobileNavLinkTitles.mylearning',
    [PAGE_NAMES.SAVED_ITEMS]: 'academyNav.heading.watchLater',
    [PAGE_NAMES.PLAYLISTS]: 'academyNav.heading.myPlaylists',
    [PAGE_NAMES.CREATE_PLAYLIST]: 'academyNav.heading.createPlaylist',
    [PAGE_NAMES.TEAM]: 'academyNav.heading.mobileNavLinkTitles.team',
    [PAGE_NAMES.PARTNER]: 'academyNav.heading.mobileNavLinkTitles.partners'
  };
  if (!pageName) return i18nKeys[PAGE_NAMES.CATALOG];
  return i18nKeys[pageName] || i18nKeys[PAGE_NAMES.CATALOG];
};
const NavbarMobileTabs = () => {
  const isPartner = useIsPartner();
  const history = useHistory();
  const {
    backLink,
    previousPageName,
    activePageName,
    nonNavPageSelected
  } = useNavigationState();
  const getPageUrl = pageName => {
    if (nonNavPageSelected && pageName === previousPageName) {
      return backLink;
    }
    return NAVBAR_ITEM_URLS[pageName];
  };
  useEffect(() => {
    UsageTracker.track('interaction', {
      action: 'mobile-nav-viewed'
    });
  }, []);
  const handleSprocketClick = useCallback(() => {
    history.push('/');
    UsageTracker.track('interaction', {
      action: 'mobile-nav-sprocket-clicked'
    });
  }, [history]);
  return /*#__PURE__*/_jsx(NavbarSavedItemsTooltip, {
    children: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      className: "p-left-4",
      children: [/*#__PURE__*/_jsx(UIIconButton, {
        "aria-label": I18n.text(getPageI18nKey(PAGE_NAMES.CATALOG)),
        "data-onboarding": "academy-logo",
        "data-test-id": "mobile-sprocket-home-button",
        onClick: handleSprocketClick,
        style: {
          zIndex: 1
        },
        use: "unstyled",
        children: /*#__PURE__*/_jsx(UIIcon, {
          color: LORAX,
          name: "sprocket",
          size: 32,
          style: {
            marginTop: '8px'
          }
        })
      }), /*#__PURE__*/_jsxs(Dropdown, {
        children: [/*#__PURE__*/_jsx(MobileDropdownButton, {
          "data-test-id": "mobile-dropdown-open-button",
          onClick: handleDropdownOpen,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getPageI18nKey(activePageName)
          })
        }), /*#__PURE__*/_jsxs(MobileDropdownMenu, {
          "data-test-id": "mobile-dropdown-menu",
          onClose: handleDropdownClose,
          children: [/*#__PURE__*/_jsx(MobileDropdownLinkItem, {
            "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.CATALOG}`,
            onClick: handleDropdownItemClick(PAGE_NAMES.CATALOG),
            to: getPageUrl(PAGE_NAMES.CATALOG),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(PAGE_NAMES.CATALOG)
            })
          }, PAGE_NAMES.CATALOG), /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
            "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.MY_LEARNING}`,
            onClick: handleDropdownItemClick(PAGE_NAMES.MY_LEARNING),
            to: getPageUrl(PAGE_NAMES.MY_LEARNING),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(PAGE_NAMES.MY_LEARNING)
            })
          }, PAGE_NAMES.MY_LEARNING), /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
            "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.SAVED_ITEMS}`,
            onClick: handleDropdownItemClick(PAGE_NAMES.SAVED_ITEMS),
            to: getPageUrl(PAGE_NAMES.SAVED_ITEMS),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(PAGE_NAMES.SAVED_ITEMS)
            })
          }, PAGE_NAMES.SAVED_ITEMS), /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
            "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.PLAYLISTS}`,
            onClick: handleDropdownItemClick(PAGE_NAMES.PLAYLISTS),
            to: getPageUrl(PAGE_NAMES.PLAYLISTS),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(PAGE_NAMES.PLAYLISTS)
            })
          }, PAGE_NAMES.PLAYLISTS), /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
            "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.TEAM}`,
            onClick: handleDropdownItemClick(PAGE_NAMES.TEAM),
            to: getPageUrl(PAGE_NAMES.TEAM),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(PAGE_NAMES.TEAM)
            })
          }, PAGE_NAMES.TEAM), isPartner && /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
            "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.PARTNER}`,
            onClick: handleDropdownItemClick(PAGE_NAMES.PARTNER),
            to: getPageUrl(PAGE_NAMES.PARTNER),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(PAGE_NAMES.PARTNER)
            })
          }, PAGE_NAMES.PARTNER), !isHubSpotNativeApp && /*#__PURE__*/_jsx(MobileDropdownButtonItem, {
            "data-test-id": "mobile-dropdown-item-academy-to-software",
            onClick: handleAcademyToSoftwareClick,
            children: /*#__PURE__*/_jsx(AcademyToSoftware, {
              use: "inline"
            })
          })]
        })]
      })]
    })
  });
};
export default NavbarMobileTabs;